import {
  getSessionEstimatedArrivalStatus,
  setSessionEstimatedArrivalStatus,
} from "./product.utils.js";

class VariantEstimatedArrival extends HTMLElement {
  constructor() {
    super();

    // Check if sessionStorage is available to update initial arrival state
    const arrival = getSessionEstimatedArrivalStatus() || undefined;

    this.state = {
      arrival,
    };
    this.allSizesAreWaitlist = false;
  }

  connectedCallback() {
    this.initializeElements();
    this.initializeListeners();
    this.render();
  }

  render() {
    this.updateState();
    this.updateUI();
    this.notifyArrivalModal();
  }

  /**
   * Updates the state with the selected variant's arrival time.
   */
  updateState() {
    const variantOptionConfig = {
      arrival: {
        selector: ".estimated-arrival-input",
        defaultToFirstAvailable: false,
      },
    };

    const sessionArrival = getSessionEstimatedArrivalStatus();

    if (sessionArrival) {
      this.state.arrival = sessionArrival;
    } else {
      // Set properties for this.state
      Object.entries(variantOptionConfig).forEach(
        ([variantOptionKey, config]) => {
          this.state[variantOptionKey] = this.getVariantOptionValue(
            config.selector,
            config.defaultToFirstAvailable,
          );
        },
      );
    }
  }

  /**
   * Notifies the estimated arrival modal about the state update.
   */
  notifyArrivalModal() {
    const event = new CustomEvent("arrivalStateUpdated", {
      detail: this.state,
    });
    const arrivalModal = this.querySelector("arrival-modal");
    if (arrivalModal) {
      arrivalModal.dispatchEvent(event);
    }
  }

  /**
   * Retrieves the value of a selected variant option from the DOM.
   * @param {string} selector - The CSS selector for the input elements.
   * @param {boolean} defaultToFirstAvailable - If true, defaults to the first available option if none are selected.
   * @returns {string|undefined} The value of the selected or default option.
   */
  getVariantOptionValue(selector, defaultToFirstAvailable = false) {
    const elements = this.querySelectorAll(selector);
    const checked = Array.from(elements).find((el) => el.checked);

    if (checked) {
      return checked.value;
    }
    if (defaultToFirstAvailable && elements.length > 0) {
      return elements[0].value;
    }
    return undefined;
  }

  /**
   * Updates the label legend, message background color, and message text content based on the arrival state.
   * TO DO: Refactor into separate component.
   */
  updateUI() {
    let { arrival } = this.state;
    if (!arrival) return;
    arrival = arrival.toLowerCase();

    const { standardMessage, rushMessage } =
      this.estimatedArrivalMessage.dataset;
    if (arrival.includes("standard")) {
      this.legend.textContent = "Standard (No fee)";
      this.estimatedArrivalMessageText.textContent = String(standardMessage);
    } else {
      this.legend.textContent = `Rush +${this.rushFee}`;
      this.estimatedArrivalMessageText.textContent = String(rushMessage);
      if (this.rushFee === "$0") {
        this.standardRadio.disabled = true;
        this.standardRadio.classList.add("disable-mto");
      }
    }

    if (this.standardRadio.disabled && this.rushRadio.disabled) {
      this.estimatedArrivalMessage.classList.add("hidden");
    } else {
      this.estimatedArrivalMessage.classList.remove("hidden");
    }

    this.updateCTAtext();
    this.swapHexOfCta();
  }

  /**
   * Updates the cta text based on the arrival state.
   */
  updateCTAtext() {
    this.allSizesAreWaitlist = !Array.from(this.pdpOptions).some(
      (opt) =>
        opt.dataset.variantInventoryPolicy !== "deny" &&
        opt.dataset.quantity > 0,
    );

    let { arrival } = this.state;
    arrival = arrival.toLowerCase();
    const standardProductionIsWaitlist = this.isStandardProductionOnWaitlist();

    if (
      arrival.includes("standard") &&
      standardProductionIsWaitlist &&
      this.allSizesAreWaitlist
    ) {
      this.pdpAtc.textContent = "ADD ME TO THE WAITLIST";
    } else {
      this.pdpAtc.textContent = "ADD TO BAG";
    }
  }

  isStandardProductionOnWaitlist() {
    return this.standardRadio.classList.contains("waitlisted");
  }

  colorToATC = [
    {
      id: "WAITLIST",
      color: "#f4e6dc",
    },
    {
      id: "ADD TO BAG",
      color: "#e5b3ac",
    },
  ];

  /**
   * Changes the background color of the call to action button.
   */
  swapHexOfCta = () => {
    let { arrival } = this.state;
    arrival = arrival.toLowerCase();
    const standardProductionIsWaitlist = this.isStandardProductionOnWaitlist();

    if (this.pdpAtc) {
      const addToCartButton = this.pdpAtc;

      addToCartButton.classList.remove(
        "button-primary",
        "button-primary-alt",
        "button-secondary",
        "button-special",
      );

      // Update color based on arrival state
      addToCartButton.classList.add("button-primary");
    }
  };

  initializeElements() {
    this.product = document.querySelector(".product-wrapper");
    this.pdpAtc = this.product.querySelector("#pdp-atc");
    this.legend = this.querySelector("#estimated-arrival-legend");
    this.rushFee = this.querySelector(
      "#estimated-arrival-legend",
    ).dataset.rushFee;
    this.radios = this.querySelectorAll(
      ".estimated-arrival-radios .variant-radio",
    );
    this.sizeRadios = this.product.querySelectorAll(
      ".size-dropdown .dropdown-item",
    );
    this.labels = this.querySelectorAll(
      ".estimated-arrival-radios .product-radio-label",
    );
    this.estimatedArrivalMessage =
      this.product.querySelector("#shipping-message");
    this.estimatedArrivalMessageText =
      this.estimatedArrivalMessage.querySelector("p");
    this.estimatedArrivalMessage.classList.remove("hidden");
    this.rushRadio = null;
    this.standardRadio = null;
    const standardTitle = window.dualSaleStandardOptionName.toLowerCase();
    const rushTitle = window.dualSaleRushOptionName.toLowerCase();
    this.radios.forEach((radio) => {
      if (radio.value.toLowerCase().includes(rushTitle)) {
        this.rushRadio = radio;
      } else if (radio.value.toLowerCase().includes(standardTitle)) {
        this.standardRadio = radio;
      }
    });
    this.pdpOptions = this.product.querySelectorAll(".pdp-option");
  }

  initializeListeners() {
    this.radios.forEach((radio) => {
      const sessionArrival = getSessionEstimatedArrivalStatus();
      if (radio.value === sessionArrival || radio.checked) {
        radio.click();
      }

      radio.addEventListener("click", () => {
        setSessionEstimatedArrivalStatus(radio.value);
        this.render();
      });

      const observer = new MutationObserver((mutationsList) => {
        mutationsList.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class" &&
            window.autoDisableMTO
          ) {
            if (!radio.disabled) {
              this.getLabelForRadio(radio).style.pointerEvents = "auto";
            } else {
              this.getLabelForRadio(radio).style.pointerEvents = "none";
            }
          }

          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "disabled"
          ) {
            if (radio.disabled) {
              this.getLabelForRadio(radio).style.pointerEvents = "none";
            } else {
              this.getLabelForRadio(radio).style.pointerEvents = "auto";
            }
          }
        });
      });

      observer.observe(radio, { attributes: true });
    });

    this.sizeRadios.forEach((radio) => {
      radio.addEventListener("click", () => {
        this.render();
      });
    });
  }

  /**
   * Helper method to find the label corresponding to a radio input.
   * @param {Element} radio - The radio input element.
   * @returns {Element|null} The corresponding label element.
   */
  getLabelForRadio(radio) {
    const labelsArray = Array.from(this.labels);
    return labelsArray.find((label) => label.htmlFor === radio.id);
  }
}

export default VariantEstimatedArrival;
